import { FileUpdates, UploadedFile } from './Files';
import { RiskCategory, RiskControlMapping, RiskStrategy } from './RiskRegister';
import { DisplayableTag } from './Tags';
import { OptionType } from './Types/GlobalType';

export enum ActionStatus {
    OPEN = 'Open',
    CLOSED = 'Closed',
}

export const ActionStatusOptions: OptionType[] = [
    {
        value: ActionStatus.OPEN,
        label: ActionStatus.OPEN,
    },
    {
        value: ActionStatus.CLOSED,
        label: ActionStatus.CLOSED,
    },
];

export const actionStatusComparator = (statusA: ActionStatus, statusB: ActionStatus): number => {
    if (statusA === statusB) {
        return 0;
    } else {
        return statusA === ActionStatus.CLOSED ? 1 : -1;
    }
};

export enum TreatmentPlanStatus {
    ACTIVE = 'Active',
    COMPLETE = 'Complete',
    MISSING = 'Missing', // Note: not intended to be displayed in the UI.
}

export enum ActionsSortFilterOptions {
    ASSOCIATED_RISKS = 'associated_risks',
    DUE_DATE = 'due_date',
    LAST_UPDATED = 'last_updated',
    OWNER = 'owner',
    STATUS = 'status',
    TAGS = 'tags',
    TITLE = 'title',
}

export interface AssociatedRisk {
    id: string;
    title: string;
    description: string;
    last_updated_by: string;
    category: RiskCategory;
    control_environment_effectiveness: number;
    treatment_plan_status: TreatmentPlanStatus;
    treatment_plan_description?: string;
    controls: RiskControlMapping[];
    scores_comment?: string;
    inherent_risk_probability?: number;
    inherent_risk_impact?: number;
    total_inherent_risk?: number;
    current_risk_probability?: number;
    current_risk_impact?: number;
    total_current_risk?: number;
    target_risk_probability?: number;
    target_risk_impact?: number;
    total_target_risk?: number;
    owner?: string;
    strategy?: RiskStrategy;
}

export interface Action {
    description: string;
    id: string;
    last_updated: string;
    last_updated_by: string;
    owner: string;
    status: ActionStatus;
    title: string;

    associated_risks: AssociatedRisk[];
    closed_by?: string; // Added after the feature was implemented. Going forward this will always be present if the status is CLOSED, but some existing CLOSED records may not have this.
    closed_timestamp?: string; // Added after the feature was implemented. Going forward this will always be present if the status is CLOSED, but some existing CLOSED records may not have this.
    comments?: string;
    created_by?: string; // Added after the feature was implemented. Going forward this will always be present, but some existing records may not have this.
    created_timestamp?: string; // Added after the feature was implemented. Going forward this will always be present, but some existing records may not have this.
    delegates: string[];
    documents: UploadedFile[];
    due_date?: string;
    link?: string; // Within the UI this has been renamed to "reference" and its use has been expanded to anything, not just URLs, but the attribute on domain objects and DTOs is still "link" to avoid a data migration.
    tags: string[];
}

export interface CreateActionRequest {
    associated_risks: string[];
    description: string;
    owner: string;
    tags: string[];
    title: string;

    comments?: string;
    delegates: string[];
    due_date?: string;
    file_updates?: FileUpdates;
    link?: string; // Within the UI this has been renamed to "reference" and its use has been expanded to anything, not just URLs, but the attribute on domain objects and DTOs is still "link" to avoid a data migration.
}

export interface UpdateActionRequest {
    associated_risks: string[];
    description: string;
    file_updates: FileUpdates;
    owner: string;
    status: ActionStatus;
    tags: string[];
    title: string;

    comments?: string;
    delegates: string[];
    due_date?: string;
    link?: string; // Within the UI this has been renamed to "reference" and its use has been expanded to anything, not just URLs, but the attribute on domain objects and DTOs is still "link" to avoid a data migration.
}

// As defined by the backend, action histories have all the same properties as actions, except that tags are in a displayable format.
// (Historical tags may not match up with the client's currently configured tags, so they can't be looked up in the same way as tags on "current" items can be.)
export type ActionHistory = Omit<Action, 'tags'> & { tags: DisplayableTag[] };
